// One row of the hand-rolled pricing table (admin/CLAUDE.md: "Hand-roll the
// pricing table. Do not reach for a data-grid library"). Renders as two
// <tr>s — the priced line itself, and a compact reference sub-row beneath
// it (list/floor price, recent quotes) — so the table still reads as one
// ruled ledger document (root CLAUDE.md) rather than a card-per-line.
//
// Keyboard model (admin/CLAUDE.md, "keyboard-first"): the unit-price input
// is the one field with custom Tab/Enter/Escape handling, wired by the
// parent (WorkspacePage) via `onPriceKeyDown` — this component just renders
// the input and forwards the ref/handler, it does not own the navigation
// logic (that needs the full row list, which only the parent has).

import { useWatch } from 'react-hook-form';
import { validateQty, validateUnit, validateUnitPrice } from 'shared/schemas/pricingSchemas.js';
import { formatMoney, previewLineTotalCents, centsToDecimalString } from 'shared/money.js';
import { useLanguage } from '../context/LanguageContext.jsx';
import ProductPicker from './ProductPicker.jsx';
import PriceReference from './PriceReference.jsx';
import styles from './PricingLineRow.module.css';

export default function PricingLineRow({
  index,
  field,
  register,
  control,
  setValue,
  errors,
  requestLine,
  isNewLine,
  productsMap,
  quotationId,
  organizationTier,
  priceInputRef,
  onPriceKeyDown,
  onRemove,
}) {
  const { t } = useLanguage();

  const productId = useWatch({ control, name: `lines.${index}.productId` });
  // Live-reactive to the row's own current productId (which changes as the
  // user picks/clears a product), not a static snapshot from the row's
  // initial defaultValues — this is why the map itself is passed down
  // rather than a precomputed entry.
  const catalogEntry = productId ? productsMap.get(productId) : undefined;
  const unitPriceValue = useWatch({ control, name: `lines.${index}.unitPrice` });
  const qtyValue = useWatch({ control, name: `lines.${index}.qty` });
  const belowFloorFromServer = useWatch({ control, name: `lines.${index}.belowFloor` });

  const lineErrors = errors?.lines?.[index] || {};

  // Blank is explicitly allowed to pass field-level validation on both qty
  // and unitPrice — an unpriced line is a normal, in-progress state (a
  // sales user prices a 20-line request incrementally across several
  // Saves), not a validation failure. WorkspacePage's onSave filters blank
  // lines out of the payload entirely rather than sending them; the real
  // pattern check (qtySchema/unitPriceSchema, mirroring the backend) only
  // applies once a value has actually been typed.
  const validateQtyOrBlank = (v) => (v === '' || v === undefined ? true : validateQty(v));
  const validateUnitPriceOrBlank = (v) =>
    v === '' || v === undefined ? true : validateUnitPrice(v);

  // Registered once (not via the usual `{...register(name)}` spread) so its
  // `ref` callback can be composed with priceInputRef below — react-hook-
  // form's own `ref` and this component's own array-of-refs (needed for the
  // Tab/Enter/Escape-down-the-price-column keyboard model) both need to see
  // the same DOM node.
  const priceField = register(`lines.${index}.unitPrice`, { validate: validateUnitPriceOrBlank });

  const previewTotal =
    qtyValue && unitPriceValue !== undefined && unitPriceValue !== ''
      ? centsToDecimalString(previewLineTotalCents(qtyValue, unitPriceValue))
      : null;

  const matchLabel =
    !isNewLine && requestLine
      ? requestLine.matched_product_id
        ? t('workspace.matchConfidence', {
            value: requestLine.match_confidence
              ? Math.round(Number(requestLine.match_confidence) * 100)
              : '—',
          })
        : t('workspace.notMatched')
      : null;

  return (
    <>
      <tr className={`${styles.row} ${belowFloorFromServer ? styles.rowFlagged : ''}`}>
        <td className={`${styles.lineNo} tabularNums`}>{index + 1}</td>
        <td className={styles.descriptionCell}>
          {isNewLine ? (
            <input
              type="text"
              className={styles.textInput}
              placeholder={t('workspace.col.description')}
              {...register(`lines.${index}.description`)}
            />
          ) : (
            <span className={styles.description}>{field.description}</span>
          )}
          {matchLabel && <span className={styles.matchBadge}>{matchLabel}</span>}
        </td>
        <td className={styles.unitCell}>
          <input
            type="text"
            className={styles.smallInput}
            {...register(`lines.${index}.unit`, { validate: validateUnit })}
          />
          {lineErrors.unit && (
            <span className={styles.fieldError}>{t('error.VALIDATION_ERROR')}</span>
          )}
        </td>
        <td className={styles.qtyCell}>
          <input
            type="text"
            inputMode="decimal"
            className={`${styles.smallInput} tabularNums`}
            {...register(`lines.${index}.qty`, { validate: validateQtyOrBlank })}
          />
          {lineErrors.qty && (
            <span className={styles.fieldError}>{t('error.VALIDATION_ERROR')}</span>
          )}
        </td>
        <td className={styles.productCell}>
          <ProductPicker
            selectedProduct={catalogEntry}
            onSelect={(product) =>
              setValue(`lines.${index}.productId`, product.id, { shouldDirty: true })
            }
            onClear={() => setValue(`lines.${index}.productId`, undefined, { shouldDirty: true })}
          />
        </td>
        <td className={styles.priceCell}>
          <input
            type="text"
            inputMode="decimal"
            className={`${styles.priceInput} tabularNums`}
            name={priceField.name}
            onChange={priceField.onChange}
            onBlur={priceField.onBlur}
            ref={(el) => {
              priceField.ref(el);
              priceInputRef(index, el);
            }}
            onKeyDown={(e) => onPriceKeyDown(e, index)}
          />
          {lineErrors.unitPrice && (
            <span className={styles.fieldError}>{t('error.VALIDATION_ERROR')}</span>
          )}
          <input
            type="text"
            className={styles.discountInput}
            placeholder={t('workspace.discountReasonPlaceholder')}
            {...register(`lines.${index}.discountReason`)}
          />
          {lineErrors.discountReason && (
            <span className={styles.fieldError}>{t('error.VALIDATION_ERROR')}</span>
          )}
        </td>
        <td className={`${styles.totalCell} tabularNums`}>
          {previewTotal !== null ? formatMoney(previewTotal) : '—'}
        </td>
        <td className={styles.removeCell}>
          {isNewLine && (
            <button type="button" className={styles.removeButton} onClick={onRemove}>
              {t('workspace.actions.removeLine')}
            </button>
          )}
        </td>
      </tr>
      <tr className={styles.referenceRow}>
        <td />
        <td colSpan={7}>
          {productId ? (
            <PriceReference
              productId={productId}
              catalogEntry={catalogEntry}
              currentValue={unitPriceValue}
              quotationId={quotationId}
              organizationTier={organizationTier}
            />
          ) : (
            <p className={styles.freeTextHint}>{t('workspace.freeText')}</p>
          )}
        </td>
      </tr>
    </>
  );
}
